import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { TextInput, Button } from '@dsg-tech/homefield-react';
import { useAppDispatch, useAppSelector } from 'store/store';
import { getFeatureFlagByNameSelector, storeIdentifierNameSelector } from 'store/selectors';
import { setView, addNewList } from 'store/slices/listsPanel';
import { newListsSelector } from 'store/selectors/listsPanelSelector';
import { favoritesListsSelector } from 'store/selectors/userFavoritesSelector';
import { useDebounce } from 'use-debounce';
import { siteErrorEvent } from 'utility/analytics-events';
import BannedWords from 'utility/banned-words.json';
import useLists from 'hooks/useLists';
import Switch from './Switch';

const ListForm = () => {
  const msgError = {
    ErrorMessage: 'List name not good',
  };
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const [errorMessage, setErrorMessage] = useState('');
  const [listNameValue, setListNameValue] = useState('');
  const { createList } = useLists(storeIdentifierName);
  const inputRef = useRef(null);
  const dispatch = useAppDispatch();
  const hasNameChanged = useMemo(() => listNameValue.toLowerCase().trim() !== '', [listNameValue]);
  const athleteLists = useAppSelector(favoritesListsSelector);
  const newLists = useAppSelector(newListsSelector);
  const shareListEnabled = useAppSelector(getFeatureFlagByNameSelector('shareListEnabled'));
  const [listVisibility, setListVisibility] = useState(false);
  const [debouncedListValueName] = useDebounce(listNameValue, 200);

  const validateListNameExist = useCallback(
    (listName: string) => {
      const isDuplicated = [...athleteLists, ...newLists].some(
        (list) => list.displayName.toLowerCase().trim() === listName.toLowerCase().trim(),
      );

      if (isDuplicated) {
        setErrorMessage('List name already taken!');
        siteErrorEvent(msgError);
      } else {
        setErrorMessage('');
      }
    },
    [athleteLists, newLists],
  );

  // ValidateBannedWords filters out banned words when creating a list name.
  // RegExp checks for special characters in banned words list like '|' or '$' and words boundary to avoid a word like 'title' send error.

  const validateBannedWords = (listName: string) => {
    const words = BannedWords.BannedWords.some((value) => {
      const escapeRegExp = new RegExp(
        `(?<![\\w\\d])${value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}(?![\\w\\d])`,
        'i',
      );

      return !!escapeRegExp.exec(listName.toLowerCase());
    });

    if (words) {
      setErrorMessage('Uh-oh, looks like you used a prohibited word. Please revise & try again');
      siteErrorEvent(msgError);
    } else {
      setErrorMessage('');
      validateListNameExist(listName);
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    validateBannedWords(debouncedListValueName);
  }, [debouncedListValueName]);

  const AddListNameHandler = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { value } = e.target;
    if (value.length > 50) {
      setErrorMessage('List name is too long');
      siteErrorEvent(msgError);
    } else {
      setErrorMessage('');
      setListNameValue(value);
    }
  };

  const toggleCallback = useCallback((e) => {
    setListVisibility(e);
  }, []);

  const editHandler = useCallback(async () => {
    const listResult = await createList(listNameValue, listVisibility);
    if (listResult) {
      dispatch(
        addNewList({
          id: listResult.id,
          displayName: listResult.name || listResult.displayName,
          selected: true,
          productID: undefined,
          defaultList: listResult.defaultList,
          productCount: 0,
          createdDate: listResult.createdDate,
        }),
      );
      // no matter if original form was add or delete
      dispatch(setView('add-product'));
    }
  }, [listNameValue, listVisibility]);

  return (
    <div className="hmf-p-m hmf-align-self-stretch">
      <TextInput
        type="text"
        ref={inputRef}
        data-testid="text-input-list-name"
        onChange={AddListNameHandler}
        value={listNameValue}
        placeholder="What is your list name?"
        helperText={errorMessage || ''}
        name="create list input"
        error={!!errorMessage}
        errorIconEnabled={!!errorMessage}
      />
      {shareListEnabled && (
        <div className="list-form-row hmf-display-flex hmf-flex-row hmf-mt-s">
          <div className="hmf-display-flex hmf-flex-column hmf-flex-grow">
            <div className="hmf-subheader-m align-content-flex-start">Make List Public</div>
            <div className="hmf-subheader-s form-row-subtitle align-content-flex-start">
              When set to public your list can be shared and viewed by others.
            </div>
          </div>
          <Switch onChange={toggleCallback} />
        </div>
      )}
      <Button
        text="Create"
        data-testid="text-input-list-save-button"
        className="lists-panel-button hmf-m-0 hmf-mt-m"
        variant="primary"
        size="medium"
        disabled={listNameValue.length === 0 || !!errorMessage || !hasNameChanged}
        onClick={editHandler}
      />
    </div>
  );
};

export default ListForm;
